import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { 
  TrendingUp, MapPin, Target, DollarSign, Users, 
  Award, Calendar, BarChart3, PieChart, Download,
  AlertCircle, CheckCircle, Clock
} from 'lucide-react';

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('all'); // all, month, quarter, year

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('xchange_token');
    const userData = localStorage.getItem('xchange_user');

    if (!token || !userData) {
      navigate('/xchange/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'admin') {
      navigate('/xchange/login');
      return;
    }

    setUser(parsedUser);
    await fetchAnalytics(token);
  };

  const fetchAnalytics = async (token) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

      // Fetch engagements for analysis
      const engRes = await fetch(`${backendUrl}/api/saas/engagements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const engagements = await engRes.json();

      // Calculate analytics
      const analyticsData = calculateAnalytics(engagements);
      setAnalytics(analyticsData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setLoading(false);
    }
  };

  const calculateAnalytics = (engagements) => {
    // Sector analysis
    const sectorMap = {};
    const stateMap = {};
    const statusMap = { on_track: 0, at_risk: 0, off_track: 0 };
    
    let totalBudget = 0;
    let avgDuration = 0;
    
    engagements.forEach(eng => {
      // Sector
      const sector = eng.sector || 'Other';
      sectorMap[sector] = (sectorMap[sector] || 0) + 1;
      
      // State
      const state = eng.state || 'Multiple';
      stateMap[state] = (stateMap[state] || 0) + 1;
      
      // Health status
      statusMap[eng.healthStatus] = (statusMap[eng.healthStatus] || 0) + 1;
      
      // Budget
      totalBudget += eng.totalBudget || 0;
      
      // Duration
      if (eng.startDate && eng.endDate) {
        const start = new Date(eng.startDate);
        const end = new Date(eng.endDate);
        const months = (end - start) / (1000 * 60 * 60 * 24 * 30);
        avgDuration += months;
      }
    });

    avgDuration = engagements.length > 0 ? avgDuration / engagements.length : 0;

    // Top sectors
    const topSectors = Object.entries(sectorMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([sector, count]) => ({ sector, count }));

    // Top states
    const topStates = Object.entries(stateMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([state, count]) => ({ state, count }));

    return {
      totalEngagements: engagements.length,
      activeEngagements: engagements.filter(e => e.status === 'active').length,
      totalBudget,
      avgDuration: Math.round(avgDuration),
      healthStatus: statusMap,
      topSectors,
      topStates,
      monthlyTrend: generateMonthlyTrend(engagements),
      completionRate: calculateCompletionRate(engagements),
      avgBudget: engagements.length > 0 ? totalBudget / engagements.length : 0
    };
  };

  const generateMonthlyTrend = (engagements) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentYear = new Date().getFullYear();
    const trend = months.map(month => ({ month, count: 0 }));

    engagements.forEach(eng => {
      if (eng.startDate) {
        const date = new Date(eng.startDate);
        if (date.getFullYear() === currentYear) {
          trend[date.getMonth()].count += 1;
        }
      }
    });

    return trend;
  };

  const calculateCompletionRate = (engagements) => {
    const completed = engagements.filter(e => e.status === 'completed').length;
    return engagements.length > 0 ? (completed / engagements.length * 100).toFixed(1) : 0;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount * 100000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{borderColor: '#14B8A6'}}></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{color: '#1E3A5F'}}>
                Analytics & Intelligence
              </h1>
              <p className="text-gray-600">Comprehensive insights across all CSR engagements</p>
            </div>
            <button
              className="px-6 py-3 rounded-lg font-medium transition-all hover:shadow-lg flex items-center gap-2"
              style={{background: '#14B8A6', color: '#FFFFFF'}}
            >
              <Download className="w-4 h-4" />
              Export Report
            </button>
          </div>

          {/* Key Metrics */}
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: 'rgba(20, 184, 166, 0.1)'}}>
                  <BarChart3 className="w-6 h-6" style={{color: '#14B8A6'}} />
                </div>
                <div>
                  <div className="text-2xl font-bold" style={{color: '#1E3A5F'}}>
                    {analytics?.totalEngagements || 0}
                  </div>
                  <div className="text-sm text-gray-600">Total Engagements</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: 'rgba(16, 185, 129, 0.1)'}}>
                  <CheckCircle className="w-6 h-6" style={{color: '#10B981'}} />
                </div>
                <div>
                  <div className="text-2xl font-bold" style={{color: '#1E3A5F'}}>
                    {analytics?.activeEngagements || 0}
                  </div>
                  <div className="text-sm text-gray-600">Active Projects</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: 'rgba(30, 58, 95, 0.1)'}}>
                  <DollarSign className="w-6 h-6" style={{color: '#1E3A5F'}} />
                </div>
                <div>
                  <div className="text-xl font-bold" style={{color: '#1E3A5F'}}>
                    {formatCurrency(analytics?.totalBudget || 0)}
                  </div>
                  <div className="text-sm text-gray-600">Total Deployed</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: 'rgba(245, 158, 11, 0.1)'}}>
                  <Award className="w-6 h-6" style={{color: '#F59E0B'}} />
                </div>
                <div>
                  <div className="text-2xl font-bold" style={{color: '#1E3A5F'}}>
                    {analytics?.completionRate || 0}%
                  </div>
                  <div className="text-sm text-gray-600">Success Rate</div>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Health Status Distribution */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold mb-6" style={{color: '#1E3A5F'}}>
                Project Health Distribution
              </h3>
              <div className="space-y-4">
                {[
                  { label: 'On Track', value: analytics?.healthStatus?.on_track || 0, color: '#10B981', icon: CheckCircle },
                  { label: 'At Risk', value: analytics?.healthStatus?.at_risk || 0, color: '#F59E0B', icon: AlertCircle },
                  { label: 'Off Track', value: analytics?.healthStatus?.off_track || 0, color: '#EF4444', icon: AlertCircle }
                ].map((item) => {
                  const total = (analytics?.healthStatus?.on_track || 0) + 
                               (analytics?.healthStatus?.at_risk || 0) + 
                               (analytics?.healthStatus?.off_track || 0);
                  const percentage = total > 0 ? (item.value / total * 100).toFixed(1) : 0;
                  
                  return (
                    <div key={item.label}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <item.icon className="w-4 h-4" style={{color: item.color}} />
                          <span className="text-sm font-medium">{item.label}</span>
                        </div>
                        <span className="text-sm font-bold" style={{color: item.color}}>
                          {item.value} ({percentage}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="h-2 rounded-full transition-all"
                          style={{
                            width: `${percentage}%`,
                            backgroundColor: item.color
                          }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Monthly Trend */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold mb-6" style={{color: '#1E3A5F'}}>
                Monthly Engagement Trend (2025)
              </h3>
              <div className="space-y-3">
                {analytics?.monthlyTrend?.map((month, idx) => {
                  const maxCount = Math.max(...(analytics?.monthlyTrend?.map(m => m.count) || [1]));
                  const width = maxCount > 0 ? (month.count / maxCount * 100) : 0;
                  
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-12 text-xs text-gray-600 font-medium">{month.month}</div>
                      <div className="flex-1 bg-gray-100 rounded-full h-6 relative">
                        <div 
                          className="h-6 rounded-full flex items-center justify-end pr-2 transition-all"
                          style={{
                            width: `${width}%`,
                            background: 'linear-gradient(90deg, #14B8A6 0%, #10B981 100%)',
                            minWidth: month.count > 0 ? '30px' : '0'
                          }}
                        >
                          {month.count > 0 && (
                            <span className="text-xs font-bold text-white">{month.count}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sector & Geographic Analysis */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Top Sectors */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <Target className="w-5 h-5" style={{color: '#14B8A6'}} />
                <h3 className="text-lg font-semibold" style={{color: '#1E3A5F'}}>
                  Top Sectors
                </h3>
              </div>
              <div className="space-y-4">
                {analytics?.topSectors?.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-sm"
                        style={{background: '#14B8A6'}}
                      >
                        {idx + 1}
                      </div>
                      <span className="font-medium" style={{color: '#1E3A5F'}}>{item.sector}</span>
                    </div>
                    <span className="text-sm font-bold" style={{color: '#14B8A6'}}>
                      {item.count} projects
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Geographic Distribution */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-5 h-5" style={{color: '#14B8A6'}} />
                <h3 className="text-lg font-semibold" style={{color: '#1E3A5F'}}>
                  Geographic Distribution
                </h3>
              </div>
              <div className="space-y-4">
                {analytics?.topStates?.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-sm"
                        style={{background: '#1E3A5F'}}
                      >
                        {idx + 1}
                      </div>
                      <span className="font-medium" style={{color: '#1E3A5F'}}>{item.state}</span>
                    </div>
                    <span className="text-sm font-bold" style={{color: '#1E3A5F'}}>
                      {item.count} projects
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Key Insights */}
          <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-xl p-6 border border-blue-200">
            <div className="flex items-start gap-4">
              <TrendingUp className="w-8 h-8 flex-shrink-0" style={{color: '#14B8A6'}} />
              <div>
                <h3 className="text-lg font-semibold mb-3" style={{color: '#1E3A5F'}}>
                  Key Insights & Recommendations
                </h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 font-bold">•</span>
                    <span>Average project duration is <strong>{analytics?.avgDuration} months</strong>, indicating medium to long-term engagement focus</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 font-bold">•</span>
                    <span>Average budget per project: <strong>{formatCurrency(analytics?.avgBudget || 0)}</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 font-bold">•</span>
                    <span><strong>{analytics?.healthStatus?.on_track || 0}</strong> projects are on track, demonstrating strong execution capability</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 font-bold">•</span>
                    <span><strong>{analytics?.healthStatus?.at_risk || 0}</strong> projects need attention to prevent delays</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
